'use client';

import { useEffect, useRef, useState } from 'react';
import { GraduationCap, Award, Calendar, MapPin, CheckCircle, ExternalLink } from 'lucide-react';
import SectionHeader from './SectionHeader';

const EDUCATION = [
  {
    id: 1,
    degree: 'Bachelor of Information Systems',
    institution: 'Faculty of Computers and AI',
    department: 'Cairo University',
    period: '2023 – 2027',
    status: 'in-progress',
    location: 'Giza, Egypt',
    color: '#c9a87c',
    highlights: [
      'Relevant coursework: Data Structures & Algorithms, System Analysis and Design',
      'Algorithms Analysis and Design, Database Systems, Software Engineering',
      'Expected graduation: 2027',
    ],
  },
];

const CERTIFICATIONS = [
  {
    id: 1,
    title: 'SQL Basic',
    issuer: 'HackerRank',
    color: '#c9a87c',
    icon: '🗄️',
    link: 'https://www.hackerrank.com/certificates/iframe/4718a028e5c7',
    verified: true,
  },
  {
    id: 2,
    title: 'SQL Intermediate',
    issuer: 'HackerRank',
    color: '#7d9b8a',
    icon: '🗄️',
    link: 'https://www.hackerrank.com/certificates/iframe/d45a017be214',
    verified: true,
  },
  {
    id: 3,
    title: 'SQL Advanced',
    issuer: 'HackerRank',
    color: '#c47d6a',
    icon: '🗄️',
    link: 'https://www.hackerrank.com/certificates/iframe/d45a017be214',
    verified: true,
  },
  {
    id: 4,
    title: 'Programming in Python',
    issuer: 'Sprints',
    color: '#a0785a',
    icon: '🐍',
    link: 'https://drive.google.com/file/d/1l9m1MCCWQIc0VbV7vIcN4Y9Tzkt-ASPJ/view?usp=drive_link',
    verified: true,
  },
  {
    id: 5,
    title: 'Introduction to CyberSecurity',
    issuer: 'DSC',
    color: '#d4a574',
    icon: '🔒',
    link: 'https://drive.google.com/file/d/1Nfh7B-nalxZN6zV2QA3UY-sTBJCYEHhA/view?usp=drive_link',
    verified: true,
  },
  {
    id: 6,
    title: 'Git & GitHub',
    issuer: 'Almdrasa',
    color: '#c9a87c',
    icon: '⚙️',
    link: 'https://drive.google.com/file/d/1cYUU3XEtuGWlhPVgp045VTkhoeFUz27y/view?usp=drive_link',
    verified: true,
  },
];

export default function EducationSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="education" ref={sectionRef} className="py-28 xl:py-40 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div
          className={`transition-all duration-800 ease-organic ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader index="05 — Education" title="Academic" highlight="background" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-16">
          <div
            className={`lg:col-span-5 transition-all duration-800 delay-100 ease-organic ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <h3 className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-5 flex items-center gap-2">
              <GraduationCap size={14} className="text-sand" />
              University
            </h3>

            {EDUCATION.map((edu) => (
              <div key={edu.id} className="warm-card rounded-2xl p-7 xl:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-sand/5 rounded-full blur-[50px]" />

                <div className="relative">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border border-sand/30 bg-sand/10 text-sand mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-sand animate-pulse" />
                    In progress
                  </span>

                  <h4 className="font-display text-xl text-parchment font-700 mb-2 leading-tight">
                    {edu.degree}
                  </h4>
                  <p className="text-sand font-500 text-sm mb-1">{edu.institution}</p>
                  <p className="text-parchment-muted text-sm mb-5">{edu.department}</p>

                  <div className="flex flex-wrap gap-4 mb-6 text-parchment-muted text-xs">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={12} />
                      <span className="font-mono">{edu.period}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin size={12} />
                      {edu.location}
                    </span>
                  </div>

                  <ul className="space-y-2">
                    {edu.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-parchment-muted text-sm">
                        <CheckCircle size={13} className="text-sage flex-shrink-0 mt-0.5" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div
            className={`lg:col-span-7 lg:mt-16 transition-all duration-800 delay-200 ease-organic ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`}
          >
            <h3 className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-5 flex items-center gap-2">
              <Award size={14} className="text-clay" />
              Certifications
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CERTIFICATIONS.map((cert, i) => (
                <div
                  key={cert.id}
                  className={`warm-card warm-card-hover rounded-xl p-5 transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${300 + i * 80}ms` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-lg border"
                      style={{
                        backgroundColor: `${cert.color}10`,
                        borderColor: `${cert.color}25`,
                      }}
                    >
                      {cert.icon}
                    </div>
                    {cert.verified && (
                      <span className="flex items-center gap-1 text-[10px] font-mono text-sage">
                        <CheckCircle size={11} />
                        Verified
                      </span>
                    )}
                  </div>

                  <h4 className="font-display text-parchment font-600 text-sm leading-snug mb-1">
                    {cert.title}
                  </h4>
                  <p className="text-parchment-muted text-xs mb-3">{cert.issuer}</p>

                  {cert.link && (
                    <a
                      href={cert.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-hover inline-flex items-center gap-1 text-[11px] font-mono transition-colors duration-200"
                      style={{ color: cert.color }}
                    >
                      <ExternalLink size={11} />
                      View certificate
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
